import React, { SetStateAction } from 'react';

import * as ManifestUI from '@project44/manifest-ds';

import { HTMLFormProperty } from '../../../../../../external/UIFormClient/domain/abstract/HTMLFormProperty';
import {
  IFormClient,
  TInputFieldFormPropertyConstructor,
} from '../../../../../../external/UIFormClient/domain/interface';
import { UIDomainEnums } from '../../../../../../external/UILibrary';
import { ITextField } from '../../../../../../external/UILibrary/domain/interfaces';
import { UserPasswordView } from './UserPassword.view';

export type TRadioGroupConfig = {
  radioGroupProps: ManifestUI.RadioGroupProps;
  radioButtons: Array<ManifestUI.RadioProps>;
};

export type NewPasswordValidationType = {
  length: string;
  lowerCase: string;
  upperCase: string;
  number: string;
};

export class UserPasswordComponent extends HTMLFormProperty {
  public readonly radioGroupConfig: TRadioGroupConfig;
  public readonly isEditable: boolean;
  public readonly isDisplayable: boolean;
  public readonly initialValue: string;
  public readonly helperText: string | undefined;
  public readonly label: string | undefined;
  public readonly name: string | undefined;
  public readonly inputType = UIDomainEnums.HTMLFormTypeEnum.INPUT_FIELD;

  constructor(
    private readonly radioGroupButtonConfig: TRadioGroupConfig,
    private readonly payload: TInputFieldFormPropertyConstructor,
    public readonly additionalTextInputFieldProps?: Partial<ITextField>,
  ) {
    super();
    Object.assign(this, payload);
    this.radioGroupConfig = radioGroupButtonConfig;
  }

  public static newPasswordValidationState: NewPasswordValidationType = {
    length: 'unknown',
    lowerCase: 'unknown',
    upperCase: 'unknown',
    number: 'unknown',
  };

  public static readonly passwordValidate = (
    value: string,
    onSubmit: boolean,
    passwordValidations: NewPasswordValidationType,
  ): NewPasswordValidationType => {
    const errorState = onSubmit ? 'fail' : 'unknown';
    const validationState = { ...passwordValidations };

    validationState['length'] = /^.{8,}$/.test(value) ? 'pass' : errorState;

    validationState['lowerCase'] = /^(?=.*[a-z]).+$/.test(value)
      ? 'pass'
      : errorState;

    validationState['upperCase'] = /^(?=.*[A-Z]).+$/.test(value)
      ? 'pass'
      : errorState;

    validationState['number'] = /^(?=.*\d).+$/.test(value)
      ? 'pass'
      : errorState;

    return validationState;
  };

  public renderFormTypeComponent: React.FC<{
    propertyName: string;
    formClient: IFormClient;
    isLoading?: boolean;
  }> = ({ propertyName, formClient, isLoading }) => (
    <UserPasswordView
      {...{
        propertyName,
        formClient,
        isLoading,
        userPasswordComponent: this,
      }}
    />
  );

  public readonly onChangeHandler = (
    inputValue: string,
    hardCheck: boolean,
    propertyName: string,
    formClient: IFormClient,
    passwordValidationsState: [
      NewPasswordValidationType,
      React.Dispatch<SetStateAction<NewPasswordValidationType>>,
    ],
  ) => {
    const [passwordValidations, setPasswordValidations] =
      passwordValidationsState;

    const validations = UserPasswordComponent.passwordValidate(
      inputValue,
      hardCheck,
      passwordValidations,
    );

    formClient.errors[propertyName] = JSON.stringify(validations);

    setPasswordValidations(validations);

    formClient.setFieldValue(propertyName, inputValue);
  };

  public handleRadioGroupOnChange(payload: {
    value: string;
    propertyName: string;
    formClient: IFormClient;
    setSelectedView: React.Dispatch<SetStateAction<string>>;
    setPasswordValidations: React.Dispatch<
      SetStateAction<NewPasswordValidationType>
    >;
  }) {
    const {
      setSelectedView,
      setPasswordValidations,
      value,
      formClient,
      propertyName,
    } = payload;

    if (value === 'DELEGATED') {
      formClient.touched[propertyName] = undefined;
      formClient.errors[propertyName] = undefined;
      formClient.setFieldValue(propertyName, '');
      setPasswordValidations(UserPasswordComponent.newPasswordValidationState);
    }
    setSelectedView(value);
  }

  public static readonly create = (
    radioGroupConfig: TRadioGroupConfig,
    formPropertyConfig?: Partial<TInputFieldFormPropertyConstructor>,
    additionalTextInputFieldProps?: Partial<ITextField>,
  ): HTMLFormProperty => {
    const payload =
      HTMLFormProperty.configureHTMLFormProperty(formPropertyConfig);

    return new UserPasswordComponent(
      radioGroupConfig,
      payload,
      additionalTextInputFieldProps,
    );
  };
}
